import React from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/button';
import { 
  BarChart3, 
  Clock, 
  FileText, 
  Shield, 
  Zap, 
  Users,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';

const features = [
  {
    icon: BarChart3,
    title: 'Dashboard Real-Time',
    description: 'Monitore problemas ativos e métricas em tempo real com WebSocket.'
  },
  {
    icon: Clock,
    title: 'Análise por Período',
    description: 'Analise disponibilidade, MTTR e MTBF em períodos customizados.'
  },
  {
    icon: FileText,
    title: 'Relatórios PDF',
    description: 'Gere relatórios profissionais com gráficos e métricas detalhadas.'
  },
  {
    icon: Shield,
    title: 'Multi-Tenant',
    description: 'Cada empresa tem seu próprio ambiente isolado e seguro.'
  },
  {
    icon: Zap,
    title: 'Alta Performance',
    description: 'Interface rápida e responsiva com atualizações automáticas.'
  },
  {
    icon: Users,
    title: 'Gestão de Equipe',
    description: 'Convide membros da equipe e gerencie permissões.'
  }
];

const benefits = [
  'Integração direta com API do Zabbix',
  'Configuração por tenant',
  'Tema claro e escuro',
  'Exportação de relatórios em PDF',
  'Histórico de relatórios',
  'Métricas de disponibilidade SLA'
];

export function LandingPage() {
  const { isAuthenticated } = useAuthStore();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="font-bold text-2xl tracking-tight">
              OneView<span className="text-red-500">.</span>
            </span>
          </Link>
          <nav className="flex items-center gap-4">
            {isAuthenticated ? (
              <Button asChild data-testid="go-to-dashboard-btn">
                <Link to="/dashboard">
                  Ir para Dashboard
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            ) : (
              <>
                <Button variant="ghost" asChild data-testid="login-btn">
                  <Link to="/login">Entrar</Link>
                </Button>
                <Button asChild data-testid="register-btn">
                  <Link to="/register">Começar Grátis</Link>
                </Button>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-6 animate-fade-in">
            Métricas Zabbix
            <br />
            <span className="text-muted-foreground">Simplificadas</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
            Extraia, visualize e gere relatórios de disponibilidade dos seus hosts Zabbix. 
            Plataforma SaaS multi-tenant para equipes de TI.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild data-testid="hero-start-btn">
              <Link to="/register">
                Criar Conta Grátis
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild data-testid="hero-learn-btn">
              <a href="#features">Saiba Mais</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 bg-muted/50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Tudo que você precisa
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Uma plataforma completa para monitoramento e geração de relatórios Zabbix.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className="bg-card p-6 rounded-xl border shadow-sm card-hover"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
                Por que escolher OneView?
              </h2>
              <p className="text-muted-foreground text-lg mb-8">
                Desenvolvido por profissionais de TI para profissionais de TI. 
                Simplificamos a extração de métricas do Zabbix para você focar no que importa.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit) => (
                  <li key={benefit} className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-muted rounded-2xl p-8 border">
              <div className="aspect-video bg-background rounded-lg border flex items-center justify-center">
                <div className="text-center p-8">
                  <BarChart3 className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Preview do Dashboard</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
            Pronto para começar?
          </h2>
          <p className="text-primary-foreground/80 text-lg max-w-2xl mx-auto mb-10">
            Crie sua conta agora e comece a extrair métricas do seu Zabbix em minutos.
          </p>
          <Button size="lg" variant="secondary" asChild data-testid="cta-start-btn">
            <Link to="/register">
              Criar Conta Grátis
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-12">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="font-bold text-xl">
                OneView<span className="text-red-500">.</span>
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} OneView. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
